import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    useAuth,
    AuthErrorDisplay,
    parseUrlError,
    type AuthErrorInfo,
} from "@/auth";
import { Card, CardContent } from "@shared-frontend/components/ui/card";
import { Progress } from "@shared-frontend/components/ui/progress";

/**
 * Auth callback page - handles the redirect after Auth0 login
 *
 * The Auth0 SDK handles the OAuth callback automatically.
 * This page waits for the AuthContext to complete token exchange
 * and then redirects to the appropriate page.
 */
function AuthCallbackPage() {
    const [error, setError] = useState<AuthErrorInfo | null>(null);
    const navigate = useNavigate();

    // Platform auth context - handles token exchange automatically
    const {
        isAuthenticated: platformAuthenticated,
        isLoading: platformLoading,
        error: platformError,
        pendingTenantSelection,
        clearError,
    } = useAuth();

    // Handle navigation after auth is ready
    useEffect(() => {
        // Wait for platform auth to finish loading
        if (platformLoading) return;

        // If there's an error from the platform, show it
        if (platformError) {
            setError(platformError);
            return;
        }

        // Check URL params for errors (e.g., from Auth0 callback)
        const urlError = parseUrlError(
            new URLSearchParams(window.location.search),
        );
        if (urlError) {
            setError(urlError);
            return;
        }

        // If tenant selection is required, redirect there
        if (pendingTenantSelection) {
            navigate("/select-tenant", { replace: true });
            return;
        }

        // If platform authenticated, we're done
        if (platformAuthenticated) {
            // Check if there was a saved redirect location
            const savedLocation = sessionStorage.getItem("auth_redirect");
            sessionStorage.removeItem("auth_redirect");

            // Only allow relative paths to prevent open redirect attacks.
            // Reject absolute URLs, protocol-relative URLs, and data/javascript URIs.
            const isRelativePath =
                savedLocation &&
                savedLocation.startsWith("/") &&
                !savedLocation.startsWith("//");

            navigate(isRelativePath ? savedLocation : "/", { replace: true });
        }
    }, [
        platformLoading,
        platformAuthenticated,
        platformError,
        pendingTenantSelection,
        navigate,
    ]);

    const handleRetry = () => {
        // Reload to re-trigger token exchange (Auth0 session is cached in localStorage)
        // Do NOT call login() here - that starts a new Auth0 redirect and causes a loop
        setError(null);
        clearError();
        window.location.reload();
    };

    if (error) {
        return (
            <AuthErrorDisplay
                error={error}
                onRetry={handleRetry}
                enableAutoRetry={false}
                showReturnToLogin={true}
            />
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
            <Card className="w-full max-w-md">
                <CardContent className="p-6 space-y-4">
                    <div className="flex flex-col items-center space-y-4">
                        <Progress className="h-2 w-full" />
                        <p className="text-sm text-muted-foreground">
                            Completing authentication...
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default AuthCallbackPage;
